import React, { useState, useEffect, useRef } from 'react';
import { fetchBookstore } from '../utils/bookstore';
import Loader from './Loader';
import Drawer from './Drawer';
import CRNSectionCard from './CRNSectionCard';
import {
    createMarkup,
    getCriticalDatesForItem
} from '../utils/formatters';
import CRNCentricSectionCard from './CRNCentricSectionCard';
import CRNColumns from './CRNColumns';

function SectionCards({ data, loading, selectedTerm, layoutType }) {
    const [visibleSubjects, setVisibleSubjects] = useState({});
    const [visibleCount, setVisibleCount] = useState(50);
    const loadMoreRef = useRef();

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedCRN, setSelectedCRN] = useState(null);

    const [bookstoreInfo, setBookstoreInfo] = useState(null);
    const [bookstoreLoading, setBookstoreLoading] = useState(false);

    const [instructorDrawerOpen, setInstructorDrawerOpen] = useState(false);
    const [instructorData, setInstructorData] = useState(null);

    // Sorting state for each courseKey
    const [sortingState, setSortingState] = useState({});

    // Handle sorting for a specific courseKey
    const handleSort = (courseKey, field) => {
        setSortingState(prev => {
            const currentSort = prev[courseKey];
            const isAsc = currentSort?.field === field && currentSort?.order === 'asc';

            return {
                ...prev,
                [courseKey]: {
                    field,
                    order: isAsc ? 'desc' : 'asc'
                }
            };
        });
    };

    // Get sort icon for display
    const getSortIcon = (courseKey, field) => {
        const currentSort = sortingState[courseKey];
        if (currentSort?.field !== field) return '↕';
        return currentSort.order === 'asc' ? '↑' : '↓';
    };

    // Sort CRN groups based on sorting state
    const getSortedCRNGroups = (crnGroups, courseKey) => {
        const currentSort = sortingState[courseKey];
        if (!currentSort) return Object.entries(crnGroups);

        const entries = Object.entries(crnGroups);

        return entries.sort(([, sectionsA], [, sectionsB]) => {
            // Get the first section from each group for comparison
            const sectionA = sectionsA[0];
            const sectionB = sectionsB[0];

            let compareValue = 0;

            switch (currentSort.field) {
                case 'status':
                    const statusA = sectionA.fwSectOpenInd || '';
                    const statusB = sectionB.fwSectOpenInd || '';
                    compareValue = statusA.localeCompare(statusB);
                    break;
                case 'crn':
                    compareValue = sectionA.crn.localeCompare(sectionB.crn);
                    break;
                case 'unit':
                    compareValue = (sectionA.creditHrLow || 0) - (sectionB.creditHrLow || 0);
                    break;
                case 'type':
                    const typeA = sectionA.schdCode || '';
                    const typeB = sectionB.schdCode || '';
                    compareValue = typeA.localeCompare(typeB);
                    break;
                case 'meet':
                    const meetA = sectionA.schdDesc || '';
                    const meetB = sectionB.schdDesc || '';
                    compareValue = meetA.localeCompare(meetB);
                    break;
                case 'time':
                    const timeA = sectionA.beginTime || 'ZZZ';
                    const timeB = sectionB.beginTime || 'ZZZ';
                    compareValue = timeA.localeCompare(timeB);
                    break;
                case 'days':
                    const daysA = [sectionA.mon, sectionA.tue, sectionA.wed, sectionA.thu, sectionA.fri, sectionA.sat, sectionA.sun].join('');
                    const daysB = [sectionB.mon, sectionB.tue, sectionB.wed, sectionB.thu, sectionB.fri, sectionB.sat, sectionB.sun].join('');
                    compareValue = daysA.localeCompare(daysB);
                    break;
                case 'room':
                    const roomA = `${sectionA.stvbldgDesc || ''} ${sectionA.roomCode || ''}`;
                    const roomB = `${sectionB.stvbldgDesc || ''} ${sectionB.roomCode || ''}`;
                    compareValue = roomA.localeCompare(roomB);
                    break;
                case 'capacity':
                    compareValue = (sectionA.enrl || 0) - (sectionB.enrl || 0);
                    break;
                case 'waitlist':
                    compareValue = (sectionA.waitCount || 0) - (sectionB.waitCount || 0);
                    break;
                case 'crosslist':
                    const crossA = sectionA.crossListGroup || '';
                    const crossB = sectionB.crossListGroup || '';
                    compareValue = crossA.localeCompare(crossB);
                    break;
                case 'instructor':
                    const instrA = sectionA.pfaName || '';
                    const instrB = sectionB.pfaName || '';
                    compareValue = instrA.localeCompare(instrB);
                    break;
                case 'ftyps':
                    const ftypsA = sectionA.ftyps || '';
                    const ftypsB = sectionB.ftyps || '';
                    compareValue = ftypsA.localeCompare(ftypsB);
                    break;
                case 'workload':
                    const workloadA = sectionA.workload || '';
                    const workloadB = sectionB.workload || '';
                    compareValue = workloadA.localeCompare(workloadB);
                    break;
                case 'duration':
                    const startA = sectionA.ptrmStartDate || '';
                    const startB = sectionB.ptrmStartDate || '';
                    compareValue = startA.localeCompare(startB);
                    break;
                case 'term':
                    const termA = sectionA.ptrmDesc || '';
                    const termB = sectionB.ptrmDesc || '';
                    compareValue = termA.localeCompare(termB);
                    break;
                default:
                    return 0;
            }

            return currentSort.order === 'asc' ? compareValue : -compareValue;
        });
    };


    useEffect(() => {
        if (drawerVisible && selectedCRN?.termCode && selectedCRN?.crn) {
            setBookstoreLoading(true);
            fetchBookstore(selectedCRN.termCode, selectedCRN.crn).then(info => {
                setBookstoreInfo(info);
                setBookstoreLoading(false);
            });
        }
    }, [drawerVisible, selectedCRN]);

    useEffect(() => {
        if (!data || typeof data !== 'object') {
            setVisibleSubjects({});
            return;
        }

        let processedData = { ...data };


        // Apply pagination
        const paginatedData = {};
        let currentCount = 0;

        for (const [subjDesc, courses] of Object.entries(processedData)) {
            if (currentCount >= visibleCount) break;

            paginatedData[subjDesc] = {};
            for (const [courseKey, group] of Object.entries(courses)) {
                if (currentCount >= visibleCount) break;

                paginatedData[subjDesc][courseKey] = {
                    ...group,
                    crnGroups: {}
                };

                for (const [crn, sections] of Object.entries(group.crnGroups)) {
                    if (currentCount >= visibleCount) break;

                    paginatedData[subjDesc][courseKey].crnGroups[crn] = sections;
                    currentCount += sections.length;
                }
            }
        }

        setVisibleSubjects(paginatedData);
    }, [data, visibleCount]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && data) {
                    // Calculate total items in data
                    let totalItems = 0;
                    for (const [subjDesc, courses] of Object.entries(data)) {
                        for (const [courseKey, group] of Object.entries(courses)) {
                            for (const [crn, sections] of Object.entries(group.crnGroups)) {
                                totalItems += sections.length;
                            }
                        }
                    }

                    if (visibleCount < totalItems) {
                        setVisibleCount((prev) => Math.min(prev + 50, totalItems));
                    }
                }
            },
            { threshold: 0, rootMargin: '200px' }
        );
        const currentLoadMoreRef = loadMoreRef.current;
        if (currentLoadMoreRef) {
            observer.observe(currentLoadMoreRef);
        }
        return () => {
            if (currentLoadMoreRef) {
                observer.unobserve(currentLoadMoreRef);
            }
        };
    }, [data, visibleCount]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && data) {
                    // Calculate total items in data
                    let totalItems = 0;
                    for (const [subjDesc, courses] of Object.entries(data)) {
                        for (const [courseKey, group] of Object.entries(courses)) {
                            for (const [crn, sections] of Object.entries(group.crnGroups)) {
                                totalItems += sections.length;
                            }
                        }
                    }

                    if (visibleCount < totalItems) {
                        setVisibleCount((prev) => Math.min(prev + 50, totalItems));
                    }
                }
            },
            { threshold: 0, rootMargin: '200px' }
        );
        const currentLoadMoreRef = loadMoreRef.current;
        if (currentLoadMoreRef) {
            observer.observe(currentLoadMoreRef);
        }
        return () => {
            if (currentLoadMoreRef) {
                observer.unobserve(currentLoadMoreRef);
            }
        };
    }, [data, visibleCount]);

    const onlinePop = {
        "Online ASYNCH": "Fully online and taught primarily through Canvas and other learning platforms. Classes do not have scheduled class meetings. Students will independently participate through online work and assignments provided by the instructor.",
        "Online ZOOM": "Fully online and taught through Canvas with required attendance at live, online Zoom meetings during scheduled class times."
    };


    return (
        <div className="flex-1 p-4 bg-pcc-lighter-gray">
            {loading ? <Loader /> : (
                <>
                    {Object.keys(visibleSubjects).length === 0 && !loading ? (
                        <p className="text-center text-pcc-dark-gray py-10">
                            {Object.keys(data || {}).length === 0 && !selectedTerm
                                ? 'Please select a term.'
                                : 'No sections found matching your criteria.'}
                        </p>
                    ) : (
                        <div>
                            {Object.entries(visibleSubjects).map(([subject, courses], subjectIdx) => (
                                <div
                                    key={subjectIdx}
                                    className="mb-12 rounded-lg border border-pcc-light-gray bg-white shadow-md"
                                >
                                    <div className="sticky top-0 z-10 bg-gray-100 px-6 py-4 border-b border-pcc-light-gray shadow-sm">
                                        <h2 className="text-xl font-bold text-pcc-dark-gray">{subject}</h2>
                                    </div>
                                    {/* Content Area for Courses */}
                                    <div className="p-4 md:p-6 space-y-6">
                                        {Object.entries(courses).map(([courseKey, group], groupIdx) => (
                                            <div key={groupIdx} className="border border-gray-300 rounded-lg bg-gray-50 shadow-sm">
                                                <div className="sticky z-8 bg-gray-200 px-4 py-3 font-semibold text-gray-700 border-b border-gray-300 shadow-sm"
                                                    style={{ top: 60 }}>
                                                    <span className="text-base">{courseKey}</span>
                                                </div>
                                                {(group.description || group.sectionNote) && (
                                                    <div className="px-4 py-3 bg-white text-sm">
                                                        {group.sectionNote && (<div className="text-gray-700" dangerouslySetInnerHTML={createMarkup(group.sectionNote)}></div>)}
                                                    </div>
                                                )}
                                                <div className="bg-white px-2">
                                                    {/* Desktop Table */}
                                                    <div className="hidden md:block">
                                                        <table className="w-full border border-gray-300 rounded-lg overflow-hidden shadow-sm bg-white">
                                                            <thead>
                                                                <tr className="bg-gradient-to-r from-pcc-cardinal to-red-700 text-white">
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'status')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Status
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'status')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'crn')}>
                                                                        <div className="flex items-center justify-between">
                                                                            CRN
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'crn')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'unit')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Unit
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'unit')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'type')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Type
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'type')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'meet')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Meet
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'meet')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'days')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Day(s)
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'days')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'time')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Time
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'time')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'room')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Room
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'room')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'capacity')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Cap
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'capacity')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'waitlist')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Waitlist
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'waitlist')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'crosslist')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Crosslist
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'crosslist')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'instructor')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Instructor(s)
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'instructor')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'ftyps')}>
                                                                        <div className="flex items-center justify-between">
                                                                            ftyps
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'ftyps')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'workload')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Workload
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'workload')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'duration')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Duration
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'duration')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                    <th className="py-3 px-3 font-semibold text-left text-xs uppercase tracking-wide cursor-pointer hover:bg-red-800 transition-colors"
                                                                        onClick={() => handleSort(courseKey, 'term')}>
                                                                        <div className="flex items-center justify-between">
                                                                            Term
                                                                            <span className="ml-1">
                                                                                {getSortIcon(courseKey, 'term')}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {getSortedCRNGroups(group.crnGroups, courseKey).map(([crn, sections]) => (
                                                                    <CRNColumns
                                                                        key={crn}
                                                                        crn={crn}
                                                                        sections={sections}
                                                                        onlinePop={onlinePop}
                                                                        setDrawerVisible={setDrawerVisible}
                                                                        setSelectedCRN={setSelectedCRN}
                                                                        setInstructorDrawerOpen={setInstructorDrawerOpen}
                                                                        setInstructorData={setInstructorData}
                                                                        renderTableBody={true}
                                                                        groupDescription={group.description}
                                                                        sortBy={sortingState[courseKey]?.field}
                                                                        sortOrder={sortingState[courseKey]?.order}
                                                                    />
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Mobile Cards */}
                                                    <div className="md:hidden">
                                                        {getSortedCRNGroups(group.crnGroups, courseKey).map(([crn, sections]) => (
                                                            <CRNColumns
                                                                key={crn}
                                                                crn={crn}
                                                                sections={sections}
                                                                onlinePop={onlinePop}
                                                                setDrawerVisible={setDrawerVisible}
                                                                setSelectedCRN={setSelectedCRN}
                                                                setInstructorDrawerOpen={setInstructorDrawerOpen}
                                                                setInstructorData={setInstructorData}
                                                                renderTableBody={false}
                                                                groupDescription={group.description}
                                                                sortBy={sortingState[courseKey]?.field}
                                                                sortOrder={sortingState[courseKey]?.order}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {data && (() => {
                        let totalItems = 0;
                        for (const [, courses] of Object.entries(data)) {
                            for (const [, group] of Object.entries(courses)) {
                                for (const [, sections] of Object.entries(group.crnGroups)) {
                                    totalItems += sections.length;
                                }
                            }
                        }
                        return visibleCount < totalItems && !loading;
                    })() && (
                            <div ref={loadMoreRef} className="h-20 flex justify-center items-center text-gray-500">Loading more sections...</div>
                        )}
                </>
            )}

            <Drawer
                isOpen={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                title={selectedCRN && `${selectedCRN.subjCode || ''} ${selectedCRN.courseNumbDisplay || selectedCRN.crseNumb || ''} - CRN ${selectedCRN.crn}`}
            >
                {selectedCRN && (
                    <div className="text-gray-700 space-y-6 px-1 h-full overflow-y-auto shadow-inner">

                        {selectedCRN.description && (
                            <section>
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Section Information</h4>
                                <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={createMarkup(selectedCRN.description)} />
                            </section>
                        )}

                        {bookstoreLoading ? (
                            <section className="pt-4 border-t border-gray-200">
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Textbooks</h4>
                                <div className="flex items-center text-sm text-gray-600 gap-2">
                                    <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading textbook information...
                                </div>
                            </section>
                        ) : (
                            <section className="pt-4 border-t border-gray-200">
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Textbooks</h4>
                                {bookstoreInfo && bookstoreInfo !== 'No Text Book' ? (
                                    <div className="space-y-1 text-sm text-gray-700">
                                        {bookstoreInfo.title && <p><strong>Title:</strong> {bookstoreInfo.title}</p>}
                                        {bookstoreInfo.edition && <p><strong>Edition:</strong> {bookstoreInfo.edition}</p>}
                                        {bookstoreInfo.author && <p><strong>Author:</strong> {bookstoreInfo.author}</p>}
                                        {bookstoreInfo.publisher && <p><strong>Publisher:</strong> {bookstoreInfo.publisher}</p>}
                                        {bookstoreInfo.link && (
                                            <a
                                                href={bookstoreInfo.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline"
                                            >
                                                View Required Textbooks
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <a
                                        href={`https://www.bkstr.com/pasadenastore/follett-discover-view/booklook?shopBy=discoverViewCourseRefId&bookstoreId=2082&courseRefId=${selectedCRN.crn}&termId=${selectedCRN.termCode}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline text-sm"
                                    >
                                        View Required Textbooks on Pasadena Bookstore Website
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                )}
                            </section>
                        )}

                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Enrollment</h4>
                            <div className="space-y-1 text-sm">
                                <p>
                                    <span className="font-medium text-gray-500 w-24 inline-block">Status:</span>
                                    <span className={`font-semibold ${selectedCRN.fwSectOpenInd === 'O' ? 'text-green-600' :
                                        selectedCRN.fwSectOpenInd === 'W' ? 'text-yellow-600' :
                                            selectedCRN.fwSectOpenInd === 'C' ? 'text-red-600' :
                                                'text-gray-600'
                                        }`}>
                                        {selectedCRN.fwSectOpenInd === 'O' ? 'Open' :
                                            selectedCRN.fwSectOpenInd === 'C' ? 'Closed' :
                                                selectedCRN.fwSectOpenInd === 'W' ? 'Waitlist Available' :
                                                    selectedCRN.fwSectOpenInd === 'R' ? 'Reserved' : 'Unknown'}
                                    </span>
                                </p>
                                <p>
                                    <span className="font-medium text-gray-500 w-24 inline-block">Enrolled:</span>
                                    {selectedCRN.enrl ?? 'N/A'} / {selectedCRN.maxEnrl ?? 'N/A'}
                                </p>
                                {(selectedCRN.fwSectOpenInd === 'W' || (selectedCRN.waitCapacity && selectedCRN.waitCapacity > 0)) && (
                                    <p>
                                        <span className="font-medium text-gray-500 w-24 inline-block">Waitlist:</span>
                                        {selectedCRN.waitCount ?? 'N/A'} / {selectedCRN.waitCapacity ?? 'N/A'}
                                    </p>
                                )}
                            </div>
                        </section>

                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-3">Critical Dates</h4>
                            <div className="space-y-1.5 text-sm">
                                {getCriticalDatesForItem(selectedCRN)
                                    .filter(date => date.value && date.value !== 'N/A')
                                    .map((dateInfo, idx) => (
                                        <div key={`drawer-crit-date-${idx}`} className="flex justify-between items-center">
                                            <span className="text-gray-600">{dateInfo.label}:</span>
                                            <span className=" px-2 py-0.5 rounded text-xs font-mono text-gray-800">{dateInfo.value}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </section>

                    </div>
                )}
            </Drawer>
            <Drawer
                isOpen={instructorDrawerOpen}
                onClose={() => setInstructorDrawerOpen(false)}
                title="Instructor Information"
            >
                <div className="text-gray-700 space-y-6 px-3 py-4 h-full overflow-y-auto shadow-inner">
                    {instructorData?.primary && (
                        <section>
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">
                                Primary Instructor
                            </h4>
                            <div className="space-y-1 text-sm text-gray-800">
                                <p className="font-medium">{instructorData.primary.name}</p>
                                {instructorData.primary.email && (
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600">{instructorData.primary.email}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {instructorData?.secondary?.length > 0 && (
                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">
                                Additional Instructor(s)
                            </h4>
                            <div className="space-y-3 text-sm text-gray-800">
                                {instructorData.secondary.map((instructor, idx) => (
                                    <div key={idx}>
                                        <p className="font-medium">{instructor.name}</p>
                                        {instructor.email && (
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600">{instructor.email}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </Drawer>
        </div>
    );
}

export default SectionCards;
